import type { Key } from 'react';
import type { ContextMenuLayerNode, LayerRulesForRefresh } from 'types/layerRefresh';
import { SURVEY_WFS_LAYER_SOURCE } from 'types/layerRefresh';

export const BLOB_REVOKE_DELAY_MS = 60_000;

export const NETWORK_GEO_PATH = 'network/sections/geo';

export function buildCacheBustedRefreshUrl(layerUrl: string): string {
  if (layerUrl.startsWith('blob:')) {
    return layerUrl;
  }
  const separator = layerUrl.includes('?') ? '&' : '?';
  return `${layerUrl}${separator}_ts=${Date.now()}`;
}

export function buildNetworkGeoLayerUrl(apiBaseUrl: string): string {
  return `${apiBaseUrl}/${NETWORK_GEO_PATH}`;
}

export function isSurveyCoverageRefresh(
  node: Pick<ContextMenuLayerNode, 'category' | 'endPoint' | 'key' | 'layerSource'>,
): boolean {
  if (node.layerSource === SURVEY_WFS_LAYER_SOURCE) {
    return true;
  }
  const endPoint = node.endPoint ?? '';
  if (/outputformat=application/i.test(endPoint)) {
    return true;
  }
  if (endPoint.startsWith('http://') || endPoint.startsWith('https://')) {
    return true;
  }
  const key = node.key ?? '';
  if (key === 'network-layer' || key.includes('+')) {
    return false;
  }
  if (key && /^[a-z0-9][a-z0-9-]*-\d/.test(key)) {
    return true;
  }
  return node.category == null;
}

/* Legend/network refresh uses esriRequest + RefreshData; survey coverage uses direct load. */
export function shouldUseRefreshDataFetch(
  node: Pick<ContextMenuLayerNode, 'category' | 'endPoint' | 'key' | 'layerSource'>,
): boolean {
  return !isSurveyCoverageRefresh(node);
}

export function resolveContextMenuLayerUrl(
  node: Pick<ContextMenuLayerNode, 'category' | 'endPoint' | 'key' | 'layerSource'>,
  layerRules: LayerRulesForRefresh | undefined,
  apiRootUrl: string,
): string {
  if (isSurveyCoverageRefresh(node)) {
    return node.endPoint ?? '';
  }

  const rulesUrl = layerRules?.url;
  const isBlobUrl =
    typeof rulesUrl === 'string' && rulesUrl.startsWith('blob:');
  const baseUrl = `${apiRootUrl}/${node.endPoint ?? ''}`;
  return !isBlobUrl && rulesUrl ? rulesUrl : baseUrl;
}

export function enrichContextMenuNode(
  node: ContextMenuLayerNode,
  existingLayerUrl?: string,
): ContextMenuLayerNode {
  const fallbackUrl =
    existingLayerUrl && !existingLayerUrl.startsWith('blob:')
      ? existingLayerUrl
      : undefined;
  if (!fallbackUrl || node.endPoint) {
    return node;
  }
  return { ...node, endPoint: fallbackUrl };
}

export function normalizeFavouritesContextNode(
  contextNode?: ContextMenuLayerNode | null,
): ContextMenuLayerNode | undefined {
  if (!contextNode?.key?.startsWith('favourites_')) {
    return contextNode ?? undefined;
  }
  return { ...contextNode, key: contextNode.key.replace('favourites_', '') };
}

export function resolveSelectedLayerKey(
  selectedKeys: readonly Key[],
  contextNode?: ContextMenuLayerNode | null,
): { readonly rawKey: string; readonly layerKey: string } | null {
  const firstKey = selectedKeys[0];
  const rawFromKey =
    firstKey === undefined || firstKey === null
      ? undefined
      : typeof firstKey === 'string'
        ? firstKey
        : String(firstKey);
  const rawSelectedNodeKey = rawFromKey || contextNode?.key;
  if (!rawSelectedNodeKey) {
    return null;
  }
  const layerKey = rawSelectedNodeKey.replace('favourites_', '');
  return { rawKey: rawSelectedNodeKey, layerKey };
}

export function getLoadingKeysForRefresh(
  rawKey: string,
  layerKey: string,
): readonly string[] {
  return rawKey === layerKey ? [layerKey] : [rawKey, layerKey];
}

export function createGeoJsonBlobUrl(geoJson: unknown): string {
  const blob = new Blob([JSON.stringify(geoJson)], {
    type: 'application/json',
  });
  return URL.createObjectURL(blob);
}

export function scheduleBlobUrlRevoke(
  blobUrl: string,
  delayMs: number = BLOB_REVOKE_DELAY_MS,
): void {
  setTimeout(() => URL.revokeObjectURL(blobUrl), delayMs);
}
